/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext, IGeneric } from '@msdyn365-commerce/core';
import { FeatureState, StoreOperationsDataActions } from '@msdyn365-commerce/retail-proxy';

import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';

/**
 * Input class for the getFeatureState data action.
 * The life time of the data action is 'application'.
 */
export class FeatureStateInput implements IActionInput {
    // Any additional feature names that needs to be checked in the code for enablement needs to be appended here:
    public readonly featureNames: string[] = [
        'Dynamics.AX.Application.RetailB2BEcommerceFeature',
        'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature',
        'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature',
        'Dynamics.AX.Application.RetailSearchPriceRangeFeature',
        'Dynamics.AX.Application.RetailSalesAgreementPriceSupportFeature',
        'Dynamics.AX.Application.ChannelMultipleCatalogsFeature',
        'Dynamics.AX.Application.RetailAsyncAddressCreationFeature'
    ];

    /**
     * Get Cache Key.
     * @returns - Cache key string.
     */
    public getCacheKey = (): string => 'FeatureState';

    /**
     * Get Cache type Name.
     * @returns - Cache name string.
     */
    public getCacheObjectType = (): string => 'FeatureState';

    /**
     * Get Cache type.
     * @returns - CacheType enum.
     */
    public dataCacheType = (): CacheType => 'application';
}

/**
 * CreateInput method for the getFeatureState method.
 * @param inputData -- The input data passed to the createInput method.
 * @returns - Input for data action.
 */
export const createGetFeatureStateInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new FeatureStateInput();
};

/**
 * The action method for the getFeatureState data action.
 * @param input -- The action input.
 * @param ctx -- The action context.
 * @returns -- Feature state.
 */
export async function getFeatureStateAction(input: FeatureStateInput, ctx: IActionContext): Promise<FeatureState[]> {
    const featureState = await StoreOperationsDataActions.getFeatureStatesAsync({ callerContext: ctx,
        queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx) }, input.featureNames);
    return featureState;
}

/**
 * The getFeatureState data action.
 * Gets the feature state via the read RetailServer API.
 * @returns Address information associated with the retrieved customer.
 */
export const getFeatureStateActionDataAction = createObservableDataAction<FeatureState[]>({
    id: '@msdyn365-commerce-modules/retail-actions/get-feature-state',
    action: getFeatureStateAction as IAction<FeatureState[]>,
    input: createGetFeatureStateInput
});

export default getFeatureStateActionDataAction;
